'use client';
import { CartHorizontalScroll } from './CartHorizontalScroll';

import { type ElementType } from 'react';
import {
  createReviewStatsMap,
  filterCartRecommendations,
  isRecommendationRequestRejected,
  useReviewService,
  useRegionCurrency,
  useProductService,
  type Currency,
  type CartItemResponse,
} from '@skin-seoul/service';
import { usePersonalizationTracker } from '@skin-seoul/utils';
import { Button } from '@skin-seoul/design-system';
import { CartRecommendationsSkeleton } from './CartRecommendations.Skeleton';
import { CartDiscoveryProductCard } from './CartDiscoveryProductCard';
import { CartSectionAnalytics } from './CartAnalytics';

interface CartRecommendationsProps {
  items: CartItemResponse[];
  onCartMutation?: (mutation: Promise<boolean>) => void;
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
}

export function CartRecommendations(props: CartRecommendationsProps) {
  const { country } = useRegionCurrency();
  const tracker = usePersonalizationTracker();
  const actorId = tracker.getActorId() ?? undefined;
  // Never retain a bag visit’s recommendations across identity or currency changes.
  return (
    <CartRecommendationsForScope
      key={JSON.stringify([country, props.currency, actorId])}
      {...props}
      actorId={actorId}
    />
  );
}

function CartRecommendationsForScope({
  items,
  currency,
  linkComponent,
  onProductClick,
  onCartMutation,
  actorId,
}: CartRecommendationsProps & { actorId?: string }) {
  const { useGetRecommendedProductsQuery } = useProductService();
  // Cart changes only filter these choices; they must not change the recommendation query.
  const query = useGetRecommendedProductsQuery({
    recommendName: 'skinseoulselects',
    limit: 99,
    actorId,
    currency,
  });
  const data = query.data;
  const products = filterCartRecommendations(data?.products ?? [], items).slice(0, 6);
  const { useGetReviewStatistics } = useReviewService();
  const { data: reviewStats } = useGetReviewStatistics({
    productIds: products.map((product) => product.productId).join(','),
  });
  const statsById = createReviewStatsMap(reviewStats);
  if (!data) {
    if (query.isError) {
      // Optional recommendations must not replace the cart with an error when the API rejects its strategy.
      if (isRecommendationRequestRejected(query.error)) {
        return null;
      }
      throw query.error;
    }
    return <CartRecommendationsSkeleton />;
  }
  if (!products.length) {
    return null;
  }
  return (
    <CartSectionAnalytics placement="cart-upsell" count={products.length}>
      <section aria-label="You may also like" className="-mx-4 border-t-8 border-neutral-50 px-4 py-5">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-label-large font-medium">You may also like</h2>
        </div>
        {query.isError && !isRecommendationRequestRejected(query.error) && (
          <div role="alert" className="mb-3 flex items-center gap-2 text-label-small">
            <span>Couldn’t refresh recommendations.</span>
            <Button variant="ghost" onClick={() => void query.refetch()} disabled={query.isFetching}>
              Retry
            </Button>
          </div>
        )}
        <CartHorizontalScroll className="-mx-4 px-4 flex gap-2.5 overflow-x-auto snap-x snap-proximity scroll-px-4 pb-2 scrollbar-none">
          {products.map((product, index) => (
            <CartDiscoveryProductCard
              key={product.productId}
              product={product}
              attribution={{
                placement: 'cart-upsell',
                position: index,
                strategy: 'skinseoulselects',
                attributionToken: data.attributionToken,
              }}
              onCartMutation={onCartMutation}
              rating={statsById[product.productId]}
              currency={currency}
              linkComponent={linkComponent}
              onProductClick={onProductClick}
            />
          ))}
        </CartHorizontalScroll>
      </section>
    </CartSectionAnalytics>
  );
}
